import type { Writer } from 'protobufjs';

import { extractHTTPError, Logger } from '@gitbook/util-debug';

import type { DataSyncEncoder } from './createDataSyncEncoder';
import type { DataSyncRPCServiceName } from './createDataSyncRPCClient';
import { requireRPCRequestMessage } from './messages';
import type * as protocol from './protocol';

const logger = Logger('hive:datasync:rpc');

export interface DataSyncRPCServer<ClientData> {
    /**
     * Callback when a message is received from the client.
     */
    onMessage: (
        client: { data: ClientData; encoder: DataSyncEncoder },
        message: protocol.IRPCRequestMessage
    ) => Promise<void>;
}

type WithDecoder<T> = {
    encode: (message: T) => Writer;
    decode: (bytes: Uint8Array) => T;
};

export interface DataSyncRPCMethodHandler<ClientData, Request, Reply> {
    request: WithDecoder<Request>;
    reply: WithDecoder<Reply>;
    execute: (client: ClientData, request: Request) => Promise<Reply>;
}

export interface DataSyncRPCStreamMethodHandler<ClientData, Request, Reply> {
    request: WithDecoder<Request>;
    reply: WithDecoder<Reply>;
    execute: (client: ClientData, request: Request) => AsyncIterable<Reply>;
}

export interface DataSyncRPCServerMethods<ClientData> {
    // TODO: improve typing but the protobufjs types are a bit weird
    [MethodName: string]:
        | DataSyncRPCMethodHandler<ClientData, any, any>
        | DataSyncRPCStreamMethodHandler<ClientData, any, any>;
}

function checkIsAsyncIterable<T>(input: any): input is AsyncIterable<T> {
    return input && typeof input[Symbol.asyncIterator] === 'function';
}

/**
 * Create a DataSync RPC server for a given service.
 */
export function createDataSyncRPCServer<ClientData, Name extends DataSyncRPCServiceName>(
    serviceName: Name,
    methods: DataSyncRPCServerMethods<ClientData>,
    subscriptionId?: number
): DataSyncRPCServer<ClientData> {
    return {
        onMessage: async (client, message) => {
            const rpcRequest = requireRPCRequestMessage(message);
            const method = methods[rpcRequest.method];
            if (!method) {
                logger.warning(`No method ${rpcRequest.method} on service ${message.service}`);
                return;
            }

            logger.info(
                `received RPC request ${serviceName}.${rpcRequest.method}: ${rpcRequest.id}`
            );

            try {
                const request = method.request.decode(rpcRequest.payload);
                const reply = method.execute(client.data, request);

                if (checkIsAsyncIterable<any>(reply)) {
                    for await (const item of reply) {
                        const payload = method.reply.encode(item).finish();

                        client.encoder.write({
                            rpcReply: {
                                id: client.encoder.newMessageID(),
                                replyTo: rpcRequest.id,
                                payload,
                                subscriptionId,
                            },
                        });
                    }
                    client.encoder.write({
                        rpcReply: {
                            id: client.encoder.newMessageID(),
                            replyTo: rpcRequest.id,
                            // Indicate end of stream
                            payload: null,
                            subscriptionId,
                        },
                    });
                    logger.info(`RPC request ${serviceName}.${rpcRequest.method} succeeded`);
                } else {
                    const result = await reply;
                    const payload = method.reply.encode(result).finish();

                    logger.info(`RPC request ${serviceName}.${rpcRequest.method} succeeded`);
                    client.encoder.write({
                        rpcReply: {
                            id: client.encoder.newMessageID(),
                            replyTo: rpcRequest.id,
                            payload,
                            subscriptionId,
                        },
                    });
                }
            } catch (error) {
                // `extractHTTPError` handles arbitrary error values at runtime; the cast matches
                // the util-debug convention of passing caught errors through as `Error`.
                const httpError = extractHTTPError(error as Error);

                logger.error(
                    `RPC request ${serviceName}.${rpcRequest.method} failed ${httpError.statusCode}: ${
                        error instanceof Error ? error.message : String(error)
                    }`
                );
                client.encoder.write({
                    rpcReply: {
                        id: client.encoder.newMessageID(),
                        replyTo: rpcRequest.id,
                        error: {
                            code: httpError.statusCode,
                            message: httpError.message,
                        },
                        subscriptionId,
                    },
                });
            }
        },
    };
}

/**
 * Create a typed RPC method handler.
 */
export function createDataSyncRPCMethod<ClientData, Request, Reply>(
    input: DataSyncRPCMethodHandler<ClientData, Request, Reply>
): DataSyncRPCMethodHandler<ClientData, Request, Reply> {
    return input;
}
/**
 * Create a typed RPC stream method handler.
 */
export function createDataSyncRPCStreamMethod<ClientData, Request, Reply>(
    input: DataSyncRPCStreamMethodHandler<ClientData, Request, Reply>
): DataSyncRPCStreamMethodHandler<ClientData, Request, Reply> {
    return input;
}
